import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import {useNavigate} from 'react-router';

import {RolesAccount} from '@/app/auth';
import {TYPE_FORM_EVENTS} from '@/app/auth/events/TypeFormEvents';
import {useLoading} from '@/app/zustand/useLoading';
import {featureFlags} from '@/config/featureFlags';
import {PROJECT_TAGS} from '@/constants';
import {ScheduleCardRequest} from '@/dataSource/src/api/cards/entities';
import {useCardsRepository} from '@/dataSource/src/api/cards/useRepository';
import {IAddress} from '@/dataSource/src/api/users/entities';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';
import {IEventBaseDataSchema} from '@/services';
import {CRequestCardTypeFormId} from '@/services/typeForm/models/constants';
import {TypeFormService} from '@/services/typeForm/TypeFormService';
import {WEB_VIEW_MESSAGES} from '@/services/webview/constants';
import {WebViewService} from '@/services/webview/WebViewService';

import {DEFAULT_CARD} from '../../constants/OnboardingConstants';
import {IKid} from '../AddKid/model/AddKidMode';
import {getKidData} from '../AddKid/services/handleKidInformation';

import {useTrackers} from './trackers';

type ViewModelContextType = {
  kidData: Partial<IKid>;
  shippingAddress: IAddress;
  handleConfirmShippingAddress: () => void;
  handleTypeForm: () => void;
};

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

const REQUEST_PHYSICAL_STATUS = {
  NOT_REQUESTED: 'EMBOSSING_PAUSED',
};

const VirtualCardReadyRoute = '/onboarding/virtual-card-ready';
const DownloadAppRoute = '/onboarding/download-app';

export function ViewModelProvider({children}: {children: ReactNode}) {
  //#region [---- [REPOSITORIES] ----]
  const {stopLoading} = useLoading();
  const navigate = useNavigate();
  const {profile} = useUsersRepository();
  const {openPopup} = TypeFormService();
  const kidData = getKidData(profile);
  const selectedTheme =
    profile?.metadata?.ONBOARDING_CARD_THEME_SELECTED || DEFAULT_CARD;
  const {
    cards,
    isSchedulePhysicalCardSuccess,
    handleGetCards,
    handleSchedulePhysicalCard,
  } = useCardsRepository();
  const {
    trackSelectRequestPhysicalCard,
    trackSelectRequestChangeShippingAddress,
  } = useTrackers();
  //#endregion

  //#region [---- [HOOKS] ----]
  const isEnabledRequestPhysicalCard =
    featureFlags.isEnabledRequestPhysicalCard;

  const shippingAddress = useMemo(() => {
    if (profile) {
      return profile?.shippingAddress;
    }
  }, [profile]);

  const kidId = useMemo(() => {
    if (kidData?.uuid) {
      return kidData?.uuid;
    }
    if (cards?.length > 0) {
      return cards[0]?.user_id;
    }
    return null;
  }, [cards, kidData?.uuid]);

  const kidCard = useMemo(() => {
    if (cards?.length > 0 && kidId) {
      return cards.find(item => item?.user_id === kidId);
    }
    if (cards?.length > 0) {
      return cards[0];
    }
    return null;
  }, [cards, kidId]);

  useEffect(() => {
    if (!isEnabledRequestPhysicalCard) {
      navigate(DownloadAppRoute);
      return;
    }

    if (
      kidCard &&
      kidCard?.physical_card_status !== REQUEST_PHYSICAL_STATUS.NOT_REQUESTED
    ) {
      navigate(VirtualCardReadyRoute);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kidCard]);

  useEffect(() => {
    if (!kidId || !selectedTheme) return;

    if (
      WebViewService.isWebViewActive() &&
      typeof window.ReactNativeWebView !== 'undefined' &&
      window.ReactNativeWebView
    ) {
      stopLoading();
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          status: WEB_VIEW_MESSAGES.CONTINUE_ONBOARDING,
          kidUuid: kidId,
          choseDesign: selectedTheme,
          previousScreen: 'addKidRequestPhysicalCard viewModelContext',
        }),
      );
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kidId, selectedTheme]);

  const baseSchema: IEventBaseDataSchema = useMemo(() => {
    return {
      project_tag: PROJECT_TAGS.ONBOARDING,
      FamilyMember: {
        id: kidId,
        role: RolesAccount.TEEN,
        name: `${kidData?.firstName} ${kidData?.lastName}`,
        email: kidData?.email,
        phone: kidData?.phone,
      },
      Banking: {
        card: {
          id: kidCard?.card_id,
          theme: {
            name: selectedTheme,
          },
        },
      },
    };
  }, [kidId, kidCard, kidData, selectedTheme]);

  useEffect(() => {
    handleGetCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSchedulePhysicalCardSuccess === true) {
      navigate(VirtualCardReadyRoute);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSchedulePhysicalCardSuccess]);
  //#endregion

  //#region [---- [LOGIC] ----]
  const handleConfirmShippingAddress = useCallback(() => {
    trackSelectRequestPhysicalCard(baseSchema);
    const requestCard: ScheduleCardRequest = {
      card_id: kidCard?.card_id,
      user_id: kidId,
      selected_design: selectedTheme,
      parent_user_id: profile?.id,
    };
    handleSchedulePhysicalCard(requestCard, baseSchema);
  }, [
    baseSchema,
    handleSchedulePhysicalCard,
    kidCard?.card_id,
    kidId,
    profile?.id,
    selectedTheme,
    trackSelectRequestPhysicalCard,
  ]);

  const handleTypeForm = useCallback(() => {
    trackSelectRequestChangeShippingAddress(baseSchema);
    openPopup({
      id: CRequestCardTypeFormId,
      size: 100,
      events: {
        onReadyEvent: {
          eventName: TYPE_FORM_EVENTS.VIEW_REQUEST_ADDRESS_CHANGE_FORM,
          eventData: baseSchema,
        },
        onCloseEvent: {
          eventName: TYPE_FORM_EVENTS.SELECT_CLOSE_ON_ADDRESS_CHANGE_FORM,
          eventData: baseSchema,
        },
        onSubmitEvent: {
          eventName: TYPE_FORM_EVENTS.SELECT_SUBMIT_ON_ADDRESS_CHANGE_FORM,
          eventData: baseSchema,
        },
      },
      afterSubmit: () => {
        navigate(DownloadAppRoute);
      },
    });
  }, [
    baseSchema,
    navigate,
    openPopup,
    trackSelectRequestChangeShippingAddress,
  ]);
  //#endregion

  return (
    <ViewModelContext.Provider
      value={{
        kidData,
        shippingAddress,
        handleConfirmShippingAddress,
        handleTypeForm,
      }}>
      {children}
    </ViewModelContext.Provider>
  );
}

export function useViewModelProvider() {
  const context = useContext(ViewModelContext);
  if (context === undefined) {
    throw new Error('Request Physical Card ViewModelContext is undefined');
  }
  return context;
}
